import { FormEvent, useState } from "react";

interface Props {
  /**
   * The minor this account is consenting for, or null for an adult signing up for themselves.
   * Presence is what decides whether parental consent is asked for at all.
   */
  athleteName: string | null;
  submitting: boolean;
  onSubmit: (consent: { privacyPolicyAccepted: true; tosAccepted: true; parentalConsentAccepted?: true }) => void;
  submitLabel?: string;
}

/**
 * The consent checkboxes, in one place.
 *
 * Deliberately shared between the signup wizard and the "you still owe us consent" gate rather than
 * duplicated. The gate used to have no form at all: it told people to reopen the invite link, which by
 * then always answered "Invite not found" because the invite had been accepted — a closed loop that left
 * a paying account permanently unable to consent (UX review F87). Giving the gate its own second version
 * of these three sentences is exactly the divergence the original comment warned about, so both callers
 * render this.
 */
export function ConsentForm({ athleteName, submitting, onSubmit, submitLabel = "Continue" }: Props) {
  const [privacyPolicyAccepted, setPrivacy] = useState(false);
  const [tosAccepted, setTos] = useState(false);
  const [parentalConsentAccepted, setParental] = useState(false);
  /** Set only after a submit attempt, so nobody is scolded before they've done anything. */
  const [attempted, setAttempted] = useState(false);

  const parentalRequired = athleteName !== null;
  const complete = privacyPolicyAccepted && tosAccepted && (!parentalRequired || parentalConsentAccepted);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    /**
     * Marks the attempt rather than returning silently. A primary button that does nothing at all reads
     * as a broken app, not as a missed checkbox, and it is one of the commonest ways a signup is
     * abandoned (UX review F10).
     */
    setAttempted(true);
    if (!complete) return;
    onSubmit({
      privacyPolicyAccepted: true,
      tosAccepted: true,
      ...(parentalRequired ? { parentalConsentAccepted: true as const } : {}),
    });
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Each row is two flex items -- the checkbox and one span. Leaving the text and the link as
          separate children of a flex label pushed the link to the far edge on a line of its own,
          leaving a hole mid-sentence on the three sentences that carry legal weight (F11). */}
      <label className="consent-row">
        <input type="checkbox" checked={privacyPolicyAccepted} onChange={(e) => setPrivacy(e.target.checked)} />
        <span>
          I have read and agree to the{" "}
          <a href="/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
        </span>
      </label>
      <label className="consent-row">
        <input type="checkbox" checked={tosAccepted} onChange={(e) => setTos(e.target.checked)} />
        <span>
          I have read and agree to the{" "}
          <a href="/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>
        </span>
      </label>
      {parentalRequired && (
        <label className="consent-row">
          <input type="checkbox" checked={parentalConsentAccepted} onChange={(e) => setParental(e.target.checked)} />
          <span>As the parent/guardian of {athleteName}, I give my consent for them to participate.</span>
        </label>
      )}

      {attempted && !complete && (
        <p role="alert" className="error">
          Please tick every box above to continue.
        </p>
      )}

      <button type="submit" disabled={submitting}>
        {submitting ? "Saving…" : submitLabel}
      </button>
    </form>
  );
}
